import { useCallback, useRef, useState } from 'react';
import React from 'react';
import TrackingEventPromptModal from '../widgets/TrackingEventPromptModal';
import { shouldPromptTrackingEvent } from '../utils/trackingEventControl';

/**
 * Hook that shows the tracking-event prompt when relevant fields change (or on create).
 *
 * @returns {{
 *   promptIfNeeded: (params: {
 *     isCreate: boolean;
 *     original?: Record<string, unknown>;
 *     current: Record<string, unknown>;
 *     entityType: 'shipment'|'shipment_unit'|'shipment_piece';
 *   }) => Promise<import('../utils/trackingEventControl').TrackingEventDecision|null>;
 *   trackingPromptModal: React.ReactElement;
 * }}
 */
export function useTrackingEventPrompt() {
  const [open, setOpen] = useState(false);
  const resolverRef = useRef(null);

  const close = useCallback(() => {
    setOpen(false);
    if (resolverRef.current) {
      resolverRef.current(null);
      resolverRef.current = null;
    }
  }, []);

  const resolve = useCallback((decision) => {
    setOpen(false);
    if (resolverRef.current) {
      resolverRef.current(decision);
      resolverRef.current = null;
    }
  }, []);

  const promptIfNeeded = useCallback(
    ({ isCreate, original, current, entityType }) =>
      new Promise((resolvePromise) => {
        if (!shouldPromptTrackingEvent({ isCreate, original, current, entityType })) {
          resolvePromise({ mode: 'omit' });
          return;
        }
        resolverRef.current = resolvePromise;
        setOpen(true);
      }),
    []
  );

  const trackingPromptModal = (
    <TrackingEventPromptModal open={open} onClose={close} onResolve={resolve} />
  );

  return { promptIfNeeded, trackingPromptModal };
}
